<template>
  <div class="page-admin-billing">
    <div class="page-admin-billing__card">
      <div class="page-admin-billing__card--title">{{ $t('menu.admin-billing') }}</div>

      <!--
        Current subscription. Absent rather than empty when there is no Stripe customer: a trial
        tenant, an Entreprise account invoiced elsewhere, or anyone before the backfill runs. An empty
        "your plan" panel reads as broken; no panel reads as not-applicable.
      -->
      <div v-if="myPlan" class="page-admin-billing__subscription">
        <div class="page-admin-billing__subscription--header">
          <div>
            <div class="page-admin-billing__subscription--label">
              {{ $t('page.admin-billing.current-plan') }}
            </div>
            <div class="page-admin-billing__subscription--plan">
              <span v-if="myPlan.planCode">{{ planLabel(myPlan.planCode) }}</span>
              <span v-else>{{ myPlan.priceNickname || $t('page.admin-billing.custom-plan') }}</span>
              <span v-if="myPlan.hasBiItem" class="page-admin-billing__addon">
                + {{ $t('page.admin-billing.bi-module') }}
              </span>
            </div>
          </div>
          <BaseTag v-if="myPlan.ended" :style="getSubscriptionStatusStyle('canceled')">
            {{ $t('page.admin-billing.subscription-ended-tag') }}
          </BaseTag>
          <BaseTag v-else :style="getSubscriptionStatusStyle(myPlan.status)">
            {{ $t(`page.admin-subscriptions.status.${myPlan.status}`) }}
          </BaseTag>
        </div>

        <dl class="page-admin-billing__facts">
          <template v-if="myPlan.monthlyAmount != null">
            <dt>{{ $t(myPlan.ended ? 'page.admin-billing.last-amount' : 'page.admin-billing.amount') }}</dt>
            <dd :class="{ 'page-admin-billing__muted': myPlan.ended }">{{ formatAmount(myPlan.monthlyAmount) }}</dd>
          </template>
          <template v-if="myPlan.currentPeriodEnd && !myPlan.ended">
            <dt>{{ $t('page.admin-billing.next-billing') }}</dt>
            <dd>{{ formatStandardDate(toMoment(myPlan.currentPeriodEnd)) }}</dd>
          </template>
          <template v-if="cardExpiry">
            <dt>{{ $t('page.admin-billing.card') }}</dt>
            <dd>
              {{ cardExpiry }}
              <BaseTag v-if="cardWarningTone" :style="getCardExpiryStyle(cardWarningTone)">
                {{ $t(`page.admin-billing.card-${cardWarningTone}`) }}
              </BaseTag>
            </dd>
          </template>
        </dl>

        <BaseInfoBanner
          v-if="hasPaymentIssue"
          tone="destructive"
          preset="soft"
          :title="$t('page.admin-billing.payment-failed')"
        >
          <template #icon>
            <BaseIcon name="alert-triangle" />
          </template>
          <template #action>
            <a v-if="latestInvoiceUrl" :href="latestInvoiceUrl" target="_blank" rel="noopener noreferrer">
              {{ $t('page.admin-billing.pay-invoice') }}
            </a>
            <!-- No single payable invoice: the portal lists them all. -->
            <a v-else :href="portalUrl" target="_blank" rel="noopener noreferrer">
              {{ $t('page.admin-billing.access-portal') }}
            </a>
          </template>
        </BaseInfoBanner>

        <BaseInfoBanner
          v-else-if="subscriptionEnded"
          tone="destructive"
          preset="soft"
          :title="$t('page.admin-billing.subscription-ended')"
        >
          <template #icon>
            <BaseIcon name="alert-triangle" />
          </template>
          <template #action>
            <button type="button" @click="onBookCall">{{ $t('navigation-bar.contact-us') }}</button>
          </template>
        </BaseInfoBanner>
      </div>

      <div class="page-admin-billing__portal-access">
        <div class="page-admin-billing__portal-access--title">{{ $t('page.admin-billing.access-portal') }}</div>
        <div class="page-admin-billing__portal-access--subtitle">
          {{ $t('page.admin-billing.access-portal-subtitle') }}
        </div>
        <!--
          Still the no-code portal login link. Phase 2 replaces it with a scoped portal session so an
          already-authenticated manager lands straight in, but the link itself must stay enabled in
          Stripe either way: disabling it also disables the "limit to one subscription" guard that
          stops a lapsed-trial client creating a duplicate customer.
        -->
        <BaseButton has-icon-right targetBlank :href="portalUrl">
          {{ $t('page.admin-billing.access-portal') }}
          <BaseIcon name="external-link" />
        </BaseButton>
      </div>

      <div class="page-admin-billing__troubleshoot">
        <div class="page-admin-billing__troubleshoot--title">
          {{ $t('page.admin-billing.access-problem') }}
        </div>
        <div class="page-admin-billing__troubleshoot--subtitle">
          {{ $t('page.admin-billing.access-problem-subtitle') }} <br />
          {{ $t('page.admin-billing.access-problem-subtitle-2') }}
        </div>
        <BaseButton is-outlined @click.native="toggleBeacon">
          {{ $t('page.keywords.contact') }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseInfoBanner from '@/components/base/BaseInfoBanner';
import BaseTag from '@/components/base/BaseTag.vue';
import { Beacon } from '@/plugins/beacon';
import { BI_CALENDAR_URL } from '@/util/tier-mapping';
import { formatStandardDate, toMoment } from '@/util/date-util';
import { getCardExpiryStyle, getSubscriptionStatusStyle } from '@/util/tag-tones/billing-status-tag';
import { mapActions, mapGetters } from 'vuex';

/** Stripe's no-code portal login, shared by the button and the banner fallback. */
const PORTAL_LOGIN_URL = 'https://billing.stripe.com/p/login/9AQdRg8NX4Cx04U144';

export default {
  name: 'admin-billing',
  components: { BaseButton, BaseIcon, BaseInfoBanner, BaseTag },
  data() {
    return {};
  },
  computed: {
    portalUrl: () => PORTAL_LOGIN_URL,
    ...mapGetters('billing', [
      'myPlan',
      'hasPaymentIssue',
      'subscriptionEnded',
      'latestInvoiceUrl',
      'cardExpiry',
      'cardWarningTone',
    ]),
  },
  methods: {
    ...mapActions('billing', ['getMyBilling']),
    formatStandardDate,
    toMoment,
    getCardExpiryStyle,
    getSubscriptionStatusStyle,

    /** The tier's display name, or the raw code when the locale does not know it — a tagging mistake must stay legible. */
    planLabel(code) {
      const key = `subscriptions.${code}`;
      return this.$te(key) ? this.$t(key) : code;
    },

    formatAmount(monthly) {
      return `${monthly.toFixed(2)} $ ${this.$t('page.admin-subscriptions.interval.month')}`;
    },

    /** Same booking link as the navigation bar's trial and subscription banners. */
    onBookCall() {
      if (BI_CALENDAR_URL && BI_CALENDAR_URL !== '#') {
        window.open(BI_CALENDAR_URL, '_blank', 'noopener,noreferrer');
      }
    },

    toggleBeacon() {
      this.$track.contactUs('keywords');
      Beacon.toggle();
      Beacon.navigate('ask');
    },
  },
  created() {
    // Re-fetched on entry even though the nav bar already loaded it: this is the page someone opens
    // *because* something looked wrong, so it should show current state rather than session-old state.
    this.getMyBilling();
  },
};
</script>

<style lang="scss">
.page-admin-billing {
  &__muted {
    opacity: 0.45;
  }

  padding: 32px;

  &__card {
    padding: 32px;
    background: $color-white;
    border-radius: $border-radius-root;
    box-shadow: $shadow-default;
    max-width: 800px;
    min-height: 500px;

    &--title {
      color: $color-normal;
      font-size: $font-size-xxbigger;
      font-weight: bold;
      font-family: $font-family-raleway;
    }
  }

  &__subscription {
    margin-top: 32px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    background: $color-white;
    border: 1px solid $color-border;
    border-radius: 16px;

    &--header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
    }

    &--label {
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      opacity: 0.6;
      margin-bottom: 4px;
    }

    &--plan {
      font-size: 20px;
      font-weight: 600;
      color: $color-normal;
    }
  }

  &__addon {
    font-size: 14px;
    font-weight: 400;
    opacity: 0.7;
    margin-left: 4px;
  }

  &__facts {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 6px 20px;
    margin: 0;
    font-size: 14px;

    dt {
      opacity: 0.6;
    }

    dd {
      margin: 0;
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  &__portal-access {
    margin-top: 32px;
    padding: 32px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    background: $color-neutral-pale;
    border-radius: 16px;

    &--title {
      font-size: 20px;
      font-weight: 600;
      color: $color-normal;
      margin-bottom: 8px;
    }

    &--subtitle {
      margin-bottom: 16px;
    }
  }

  &__troubleshoot {
    padding: 16px;
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;

    &--title {
      font-size: 20px;
      font-weight: 600;
      color: $color-normal;
      margin-bottom: 8px;
    }

    &--subtitle {
      margin-bottom: 16px;
    }
  }

  // Phones: boxes in the card, not 32px from it on every side; the first one at its top, the
  // card's title being the header's.
  @include screen-below('tablet') {
    &__subscription,
    &__portal-access {
      margin-top: $space-md;
      padding: $space-md;
    }

    &__card--title + * {
      margin-top: 0;
    }
  }
}
</style>
